<script lang="tsx">
import { Table, type PaginationProps, type TableProps } from 'ant-design-vue'
import { initDefaultProps } from 'ant-design-vue/lib/_util/props-util'
import { objectType } from 'ant-design-vue/lib/_util/type'
import { tableProps, type ColumnType } from 'ant-design-vue/es/table'
import { type SizeType } from 'ant-design-vue/es/config-provider'
import { paginationProps } from 'ant-design-vue/es/pagination/Pagination'
import { type ContextSlots } from 'ant-design-vue/es/table/context'
import { isEmpty, omit, pick } from 'lodash-es'
import SettingColumn from './components/settingColumn.vue'
import type { IColumns, IColumnsWithId } from './types'
import { v4 as uuidV4 } from 'uuid'
import { superRouter } from '@/tools'
import * as func from '@/func'

const changeColumnsByAlign = (columns: IColumnsWithId[], align: string): IColumnsWithId[] => {
  if (!columns) return columns
  return columns.map((item) =>
    item.align
      ? item
      : ({
          ...item,
          align,
        } as IColumnsWithId)
  )
}
type IAlign = 'left' | 'right' | 'center'
const paginationPropsValue = paginationProps()
// pagination组件的属性直接提升到props
const promotePaginationPropsList = ['current', 'pageSize', 'onUpdate:current', 'onUpdate:pageSize'] as const
type PromotePaginationProps = Pick<PaginationProps, (typeof promotePaginationPropsList)[number]>
/**
 * 新华自定义表格
 */
export default defineComponent({
  name: 'XHTable',
  inheritAttrs: false,
  props: {
    ...initDefaultProps(
      {
        ...omit(tableProps(), 'columns'),
        columns: {
          type: Array as PropType<IPermissionColumns>,
          default: [],
        },
        ...pick(paginationPropsValue, promotePaginationPropsList),
        contextSlots: objectType<ContextSlots>(),
        onPaginationChange: paginationPropsValue.onChange,
      },
      {
        rowKey: 'key',
        sticky: true,
        transformCellText({ text }) {
          if ([undefined, null].includes(text) || (typeof text === 'object' && isEmpty(text))) return '-'
          return text
        },
      }
    ),
    align: {
      type: String as PropType<IAlign>,
      default: 'center',
    },
    // 是否默认展示序号列
    showSerialNumberColumn: {
      type: Boolean,
      default: false,
    },
    // 是否展示列设置按钮。用于保存用户自定义列
    configurationColumn: {
      type: Boolean,
      default: false,
    },
    // 表格名称。展示列设置按钮时，必须设置表格名称，默认增加前缀路由名称，请保证同一路由下表格名称的唯一性
    tableName: {
      type: String,
    },
  },
  emits: ['paginationChange', 'paginationShowSizeChange'],
  setup(props, { slots, attrs, emit }) {
    // const menuStore = useMenuStore()
    const route = useRoute()
    const handlePaginationChange: PaginationProps['onChange'] = (page, pageSize) => {
      emit('paginationChange', page, pageSize)
    }
    const handlePaginationShowSizeChange: PaginationProps['onShowSizeChange'] = (current, size) => {
      emit('paginationShowSizeChange', current, size)
    }
    const paginationProps = computed(() => {
      if (props.pagination === false) return false
      const defaultPaginationProps = { 'show-quick-jumper': true, showSizeChanger: true }
      const paginationPropsFromProps = promotePaginationPropsList.reduce((pre, cur) => {
        pre[cur] = props[cur] as any
        return pre
      }, {} as PromotePaginationProps)
      return {
        ...defaultPaginationProps,
        ...paginationPropsFromProps,
        onChange: handlePaginationChange,
        onShowSizeChange: handlePaginationShowSizeChange,
        ...(props.pagination ?? {}),
      }
    })
    const checkedList = ref<string[]>([])
    const columnsWithId = ref<{ _id: string; [propsName: string]: any }[]>([])
    // 设置默认不展示字段，（初始有效）
    const columnsHiddenKeys = ref<string[]>([])
    const getKey = (item: IColumns, keyDic: Record<string, boolean>) => {
      const key = ((item as ColumnType).dataIndex || item.key) as string
      if (keyDic[key]) {
        props.configurationColumn && console.error('请检查重复字段，列设置不可重复', key)
        return uuidV4()
      }
      keyDic[key] = true
      return key
    }
    watch(
      [() => props.columns, () => superRouter.get()?.currentRoute.value],
      ([columns, route]) => {
        const permissionColumns = columns?.filter((item) => {
          // 临时处理不处理权限问题，后面处理这块逻辑
          return item.permission ? !route?.meta?.menus?.authFields?.includes(item.permission) : true
        })

        const keyDic: Record<string, boolean> = {}
        const result = permissionColumns.map((item) => ({
          ...item,
          _id: getKey(item, keyDic),
        }))
        checkedList.value = result.filter((i) => i.isChecked !== false).map((i) => i._id)
        columnsWithId.value = result.map((item) => omit(item, 'isChecked', 'permission'))
        columnsHiddenKeys.value = result.filter((i) => i.isChecked === false).map((i) => i._id)
      },
      {
        deep: true,
        immediate: true,
      }
    )

    const tableSize = ref<SizeType>(props.size ?? 'middle')
    const serialNumberColumnChecked = ref<boolean>(props.showSerialNumberColumn)
    const tableProps = computed(() => {
      const cloneTableProps = omit(props, ['pagination', ...promotePaginationPropsList, 'align', 'size'])
      const checkedColumns = changeColumnsByAlign(columnsWithId.value, props.align!)?.filter((item) => checkedList.value.includes(item._id))
      let columns: IColumns[] = checkedColumns.map((i) => omit(i, ['_id']))
      serialNumberColumnChecked.value &&
        columns.unshift({
          title: '序号',
          dataIndex: '_serialNumber',
          fixed: 'left',
          width: 44,
          align: 'center',
          customRender: ({ index }) => index + 1,
        } as IColumns)
      columns = columns.map((i) => ({ ...omit(i, 'ellipsis'), ellipsis: i.ellipsis == false ? false : true }))
      return {
        ...cloneTableProps,
        columns,
        pagination: paginationProps.value,
        size: tableSize.value,
      } as TableProps
    })
    // 一列都不显示，如果写了插槽有数据的话，会报错的
    const slotsShow = computed(() => {
      return tableProps.value.columns?.length ? slots : {}
    })

    return () => (
      <Table
        class="xh-table"
        v-slots={{
          ...slotsShow.value,
          title: props.configurationColumn
            ? (...args: any) => (
                <SettingColumn
                  columnsWithId={columnsWithId.value}
                  checkedList={checkedList.value}
                  tableSize={tableSize.value}
                  serialNumberColumnChecked={serialNumberColumnChecked.value}
                  tableName={props.tableName}
                  columnsHiddenKeys={columnsHiddenKeys.value}
                  {...{
                    'onUpdate:columnsWithId': (val: any[]) => (columnsWithId.value = val),
                    'onUpdate:checkedList': (val: string[]) => (checkedList.value = val),
                    'onUpdate:tableSize': (val: SizeType) => (tableSize.value = val),
                    'onUpdate:serialNumberColumnChecked': (val: boolean) => (serialNumberColumnChecked.value = val),
                  }}
                >
                  {slots.title?.(...args)}
                </SettingColumn>
              )
            : slots.title
              ? (...args: any) => <div>{slots.title?.(...args)}</div>
              : undefined,
        }}
        {...tableProps.value}
        {...attrs}
      />
    )
  },
})
</script>
<style lang="scss" scoped>
.xh-table {
  :deep(.ant-table-sticky-scroll) {
    display: none;
  }
}
/**分页边框颜色穿透问题 */
.xh-table
  :deep(
    :where(.css-dev-only-do-not-override-17yhhjv).ant-select-status-error:not(.ant-select-disabled):not(.ant-select-customize-input):not(
        .ant-pagination-size-changer
      )
  )
  .ant-select-selector {
  border-color: #d9d9d9 !important;
}
/**分页边框颜色穿透问题 */
.xh-table
  :deep(:where(.css-17yhhjv).ant-select-status-error:not(.ant-select-disabled):not(.ant-select-customize-input):not(.ant-pagination-size-changer))
  .ant-select-selector {
  border-color: #d9d9d9 !important;
}
</style>
